/**
 * Firebase Compatibility Shim
 * This file provides a complete compatibility layer for Firebase v11
 * to ensure that any code or dependencies that rely on the older Firebase API structure
 * can function properly, including firebaseui if it's still being used indirectly.
 */

 
import { initializeApp } from '@firebase/app';
import {
   
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  TwitterAuthProvider,
  GithubAuthProvider,
  FacebookAuthProvider,
   
  OAuthProvider,
   
  PhoneAuthProvider,
  signOut,
  onAuthStateChanged,
   
  EmailAuthProvider
} from '@firebase/auth';
import {  
  getFirestore, collection, doc, setDoc, getDoc, updateDoc, serverTimestamp } from '@firebase/firestore';
import { getFunctions, httpsCallable } from '@firebase/functions';
import getFirebase, {  
  firebaseConfig, auth, firestore, functions } from '../firebaseConfig';

// Ensure Firebase is initialized
const app = typeof window !== 'undefined' ? getFirebase() : null;

// Create auth providers
const googleProvider = new GoogleAuthProvider();
const twitterProvider = new TwitterAuthProvider();
const githubProvider = new GithubAuthProvider();
const facebookProvider = new FacebookAuthProvider();

// Create a namespace object that mimics the old Firebase v8 structure
const firebase = {
  // Core app functionality
  initializeApp: (config) => {
    return app;
  },
  app: () => app,
  
  // Auth functionality
  auth: {
    // Auth providers with their IDs
    EmailAuthProvider: {
      PROVIDER_ID: 'password',
      credential: (email, password) => ({ email, password })
    },
    PhoneAuthProvider: {
      PROVIDER_ID: 'phone',
      credential: (verificationId, verificationCode) => ({ verificationId, verificationCode })
    },
    GoogleAuthProvider: {
      PROVIDER_ID: 'google.com',
      credential: (idToken, accessToken) => ({ idToken, accessToken })
    },
    TwitterAuthProvider: {
      PROVIDER_ID: 'twitter.com',
      credential: (token, secret) => ({ token, secret })
    },
    FacebookAuthProvider: {
      PROVIDER_ID: 'facebook.com',
      credential: (token) => ({ token })
    },
    GithubAuthProvider: {
      PROVIDER_ID: 'github.com',
      credential: (token) => ({ token })
    },
    // Auth persistence
    Auth: {
      Persistence: {
        LOCAL: 'LOCAL',
        SESSION: 'SESSION',
        NONE: 'NONE'
      }
    }
  },
  
  // Firestore functionality
  firestore: {
    FieldValue: {
      serverTimestamp: () => serverTimestamp()
    },
    collection: (path) => collection(firestore, path),
    doc: (path) => doc(firestore, path)
  }
};

// Add methods to the firebase.auth object to mimic v8 API
firebase.auth = () => {
  // Make sure auth is defined and available
  if (!auth) {
    console.warn('Firebase auth is not initialized yet');
    return {};
  }
  
  const authInstance = {
    currentUser: auth.currentUser,
    signInWithEmailAndPassword: (email, password) => signInWithEmailAndPassword(auth, email, password),
    createUserWithEmailAndPassword: (email, password) => createUserWithEmailAndPassword(auth, email, password),
    signInWithPopup: (provider) => {
      let authProvider;
      switch(provider.providerId) {
        case 'google.com': authProvider = googleProvider; break;
        case 'twitter.com': authProvider = twitterProvider; break;
        case 'github.com': authProvider = githubProvider; break;
        case 'facebook.com': authProvider = facebookProvider; break;
        default: authProvider = googleProvider;
      }
      return signInWithPopup(auth, authProvider);
    },
    signOut: () => {
      // Make sure auth is properly initialized before calling signOut
      if (!auth) {
        console.error('Firebase auth is not initialized');
        return Promise.resolve();
      }
      return signOut(auth);
    },
    onAuthStateChanged: (callback) => onAuthStateChanged(auth, callback)
  };
  return authInstance;
};

// Add methods to the firebase.firestore object to mimic v8 API
firebase.firestore = () => {
  return {
    ...firestore,
    collection: (path) => collection(firestore, path),
    doc: (path) => doc(firestore, path),
    setDoc: (docRef, data) => setDoc(docRef, data),
    getDoc: (docRef) => getDoc(docRef),
    updateDoc: (docRef, data) => updateDoc(docRef, data)
  };
};

// Add methods to the firebase.functions object to mimic v8 API
firebase.functions = () => {
  // Get the functions instance using the modular API
  const functionsInstance = getFunctions();
  
  return {
    // Don't spread the functions object as it's not compatible with v11
    httpsCallable: (name) => {
      // Return a function that when called will invoke the Firebase function
      return (data) => {
        const callable = httpsCallable(functionsInstance, name);
        return callable(data);
      };
    }
  };
};

// Export the firebase object as the default export
export default firebase;

// Also export individual modules to support direct imports
export const compat = {
  auth,
  firestore,
  functions,
  app
};

// Export for firebaseui compatibility
if (typeof window !== 'undefined') {
  window.firebase = firebase;
}
